import type { ColDef, GridApi } from "ag-grid-community";
import type {
  CreateRequestBodyTypeEnumKey,
  RequestListItem as ExternalApplicationListItem,
} from "@synthesis/shared-services/external/application/types";
import type {
  InternalRequestDetailResponse,
  InternalRequestDetailResponseDeadlineControlEnumKey,
} from "@synthesis/shared-services/internal/application/types";
import type { GetRequestsSortByKey } from "@synthesis/shared-services/internal/application/types";
import type {
  GetOperatorRequestsSortByKey,
  GetRequestsSortByKey as ExternalGetRequestsSortByKey,
} from "@synthesis/shared-services/external/application/types";
import { makeAutoObservable } from "mobx";
import { makeStoreProvider } from "@synthesis/shared-utils";
import dayjs from "dayjs";
import { CellWithTooltip } from "../../components/cell-with-tooltip";
import {
  formatApplicationNumber,
  formatPersonName,
  getInternalInitiatorDisplay,
  getPersonDisplay,
  isExternalApplication,
  isInternalApplication,
} from "../../shared/utils/application.utils";
import { getDictionaryOptionLabel, type CreateApplicationSelectOption } from "../create-application-page/create-application-dictionaries.lib";
import {
  buildCreateApplicationDraftTitle,
  parseCreateApplicationAdditionalData,
  getCrossingDisplayStationSection,
  parseCrossingObjectDataFromObjectDescription,
} from "../create-application-page/create-application-page.lib";
import {
  APPLICATION_CATEGORY_LABEL,
  APPLICATION_STATUS_FALLBACK_LABEL,
  APPLICATION_TYPE_FALLBACK_LABEL,
  EXTERNAL_APPLICATION_STATUS_FALLBACK_LABEL,
  INTERNAL_APPLICATION_STATUS_FALLBACK_LABEL,
  type ApplicationStatus,
} from "./applications-page.status";
import { ApplicationDeadlineControlCell } from "./local-components/application-deadline-control-cell";
import { ApplicationRailwayCell } from "./local-components/application-railway-cell";
import { ApplicationStatusCell } from "./local-components/application-status-cell";
import { ApplicationProcessingStatusCell } from "./local-components/application-processing-status-cell";
import { EMPTY_VALUE_KEY, type TableListColumnFilterItem, type TableListColumnFilterModel } from "@synthesis/shared-ui";

// ---------------------------------- TYPES ----------------------------------

export type PopupAction = {
  type: "create-application" | "import-application";
  payload?: never;
};

// ---------------------------------- CONSTANTS ----------------------------------

export type InternalApplicationListItem = InternalRequestDetailResponse;
export type ApplicationsTableItem = ExternalApplicationListItem | InternalApplicationListItem;

export type StatCardTableFilter =
  | { type: "none" }
  | { type: "statuses"; statuses: ApplicationStatus[] }
  | { type: "needsExport" }
  | { type: "internalExported" }
  | { type: "operatorExported" }
  | { type: "operatorLoaded" };

export const INTERNAL_OPERATOR_IMPORTED_STATUSES = [
  "INTAKE_CHECK",
  "SENT_TO_NG",
  "IN_NG",
  "SENT_TO_NTP",
  "IN_NTP",
  "NTP_RESP_ASSIGNED",
  "COMMISSION_PREP",
  "DECISION_FORMING",
  "NTP_APPROVAL",
  "NG_APPROVAL",
  "DECISION_REWORK",
] as const satisfies readonly ApplicationStatus[];

const INTERNAL_OPERATOR_FINAL_STATUSES = ["REVISION_REQUIRED", "TU_ISSUED", "REJECTED"] as const satisfies readonly ApplicationStatus[];

export const matchesStatCardTableFilter = (item: ApplicationsTableItem, filter: StatCardTableFilter): boolean => {
  if (filter.type === "none") {
    return true;
  }

  const record = item as Record<string, unknown>;
  const status = record.status as ApplicationStatus | undefined;

  if (filter.type === "statuses") {
    return status !== undefined && filter.statuses.includes(status);
  }

  if (filter.type === "needsExport") {
    return record.processingFlag === "NEEDS_EXPORT";
  }

  if (filter.type === "internalExported") {
    return (
      status !== undefined &&
      (INTERNAL_OPERATOR_FINAL_STATUSES as readonly ApplicationStatus[]).includes(status) &&
      record.processingFlag !== "NEEDS_EXPORT"
    );
  }

  if (filter.type === "operatorExported") {
    return getApplicationProcessingState(item) === "EXPORTED";
  }

  if (filter.type === "operatorLoaded") {
    return getApplicationProcessingState(item) === "IMPORTED";
  }

  return true;
};

export const requiresClientSideStatCardFilter = (filter: StatCardTableFilter, options: { isExternalOperator: boolean }): boolean => {
  if (filter.type === "needsExport" || filter.type === "internalExported" || filter.type === "operatorExported" || filter.type === "operatorLoaded") {
    return true;
  }

  if (filter.type === "statuses" && options.isExternalOperator && filter.statuses.length !== 1) {
    return true;
  }

  return false;
};

export const getServerStatusFilter = (filter: StatCardTableFilter, options: { isExternalOperator: boolean }): ApplicationStatus[] | undefined => {
  if (requiresClientSideStatCardFilter(filter, options)) {
    return undefined;
  }

  if (filter.type === "statuses") {
    return filter.statuses;
  }

  return undefined;
};

type FetchApplicationsPageResult = {
  content: ApplicationsTableItem[];
  totalElements: number;
};

export const fetchApplicationsTableRows = async ({
  fetchPage,
  tableFilter,
  isExternalOperator,
  startRow,
  blockSize,
  rowMatcher,
}: {
  fetchPage: (page: number, size: number) => Promise<FetchApplicationsPageResult>;
  tableFilter: StatCardTableFilter;
  isExternalOperator: boolean;
  startRow: number;
  blockSize: number;
  rowMatcher?: (item: ApplicationsTableItem) => boolean;
}): Promise<{ rows: ApplicationsTableItem[]; totalElements: number }> => {
  const needsClientSideScan = requiresClientSideStatCardFilter(tableFilter, { isExternalOperator }) || Boolean(rowMatcher);

  if (!needsClientSideScan) {
    const page = Math.floor(startRow / blockSize);
    const response = await fetchPage(page, blockSize);
    return {
      rows: response.content ?? [],
      totalElements: toNumber(response.totalElements),
    };
  }

  const matchingRows: ApplicationsTableItem[] = [];
  let page = 0;
  const fetchSize = Math.max(blockSize, TABLE_ROWS_PER_PAGE);

  while (true) {
    const response = await fetchPage(page, fetchSize);
    const content = response.content ?? [];
    matchingRows.push(
      ...content.filter((item) => {
        const statCardMatch = requiresClientSideStatCardFilter(tableFilter, { isExternalOperator })
          ? matchesStatCardTableFilter(item, tableFilter)
          : true;

        return statCardMatch && (rowMatcher?.(item) ?? true);
      }),
    );

    const totalElements = toNumber(response.totalElements);
    const fetchedCount = (page + 1) * fetchSize;

    if (content.length < fetchSize || fetchedCount >= totalElements) {
      break;
    }

    page += 1;
  }

  return {
    rows: matchingRows.slice(startRow, startRow + blockSize),
    totalElements: matchingRows.length,
  };
};

export const toNumber = (value: bigint | number | null | undefined): number => Number(value ?? 0);

export const getSelectedListFilterIds = (model: TableListColumnFilterModel | undefined): string[] =>
  model
    ? Object.entries(model.selectedItemsMap)
        .filter(([, selected]) => selected)
        .map(([key]) => key)
    : [];

export const getPaymentFilterSelectedIds = (filterModel: Record<string, unknown> | null | undefined): string[] => {
  const paymentModel =
    (filterModel?.payment as TableListColumnFilterModel | undefined) ?? (filterModel?.paymentFlag as TableListColumnFilterModel | undefined);

  return getSelectedListFilterIds(paymentModel);
};

export const getApplicationPaymentFlagValue = (application: ApplicationsTableItem | undefined): string => {
  const record = (application ?? {}) as Record<string, unknown>;
  const paymentFlag = record.paymentCondition ?? record.paymentFlag;

  return typeof paymentFlag === "string" ? paymentFlag.trim() : "";
};

export const matchesPaymentFlagFilter = (application: ApplicationsTableItem | undefined, selectedIds: string[]): boolean => {
  if (!selectedIds.length) {
    return true;
  }

  const paymentFlag = getApplicationPaymentFlagValue(application);
  const isEmpty = !paymentFlag;

  return selectedIds.some((id) => (id === EMPTY_VALUE_KEY ? isEmpty : paymentFlag === id));
};

const getCreatedOrImportedAt = (item: ApplicationsTableItem | undefined) => {
  const record = (item ?? {}) as Record<string, unknown>;

  if (isInternalApplication(item)) {
    return (record.externalCreatedAt as string | undefined) ?? (record.createdAt as string | undefined) ?? (record.importedAt as string | undefined);
  }

  return (record.createdAt as string | undefined) ?? (record.importedAt as string | undefined);
};

const getExternalApplicantFio = (item: ExternalApplicationListItem) => formatPersonName(item.applicant);

const isObjectRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);

const formatDisplayValue = (value: unknown): string => {
  if (typeof value !== "string" || !value.trim()) {
    return "—";
  }

  return value;
};

const formatDateTime = (value: unknown): string => (typeof value === "string" && value ? dayjs(value).format("DD.MM.YYYY, HH:mm") : "—");

const formatDate = (value: unknown): string => (typeof value === "string" && value ? dayjs(value).format("DD.MM.YYYY") : "—");

const getObjectDescriptionField = (application: ApplicationsTableItem | undefined, field: string): string | undefined => {
  if (!application) {
    return undefined;
  }

  const record = application as Record<string, unknown>;
  const topLevelValue = record[field];

  if (typeof topLevelValue === "string" && topLevelValue.trim()) {
    return topLevelValue;
  }

  const objectDescription = record.objectDescription;

  if (!isObjectRecord(objectDescription)) {
    return undefined;
  }

  const objectDescriptionValue = objectDescription[field];

  if (typeof objectDescriptionValue === "string" && objectDescriptionValue.trim()) {
    return objectDescriptionValue;
  }

  return undefined;
};

/**
 * Сгенерированный заголовок черновика («Новая заявка — <тип>»), который бэкенд
 * возвращает в `title`, пока пользователь не указал название проекта.
 */
const getGeneratedDraftTitle = (application: ApplicationsTableItem | undefined): string | undefined => {
  const record = (application ?? {}) as Record<string, unknown>;
  const draftMeta = parseCreateApplicationAdditionalData(typeof record.additionalData === "string" ? record.additionalData : null);
  const categoryLabel = draftMeta?.categoryLabel?.trim();

  return categoryLabel ? buildCreateApplicationDraftTitle(categoryLabel) : undefined;
};

export const getApplicationRailwayDisplay = (
  application: ApplicationsTableItem | undefined,
  railways: CreateApplicationSelectOption[] = [],
): string => {
  const record = (application ?? {}) as Record<string, unknown>;
  const displayRailway = getObjectDescriptionField(application, "displayRailway");

  if (displayRailway) {
    return displayRailway;
  }

  const apiRailway = typeof record.railway === "string" ? record.railway.trim() : "";
  const railwayKey = apiRailway || getObjectDescriptionField(application, "railway");

  return getDictionaryOptionLabel(railways, railwayKey);
};

export const getApplicationStationSectionDisplay = (application: ApplicationsTableItem | undefined): string => {
  const record = (application ?? {}) as Record<string, unknown>;
  const apiSection = typeof record.section === "string" ? record.section.trim() : "";

  if (apiSection) {
    return apiSection;
  }

  const objectDescription = record.objectDescription;

  if (isObjectRecord(objectDescription)) {
    const category = objectDescription.category;

    if (category === "crossing") {
      const crossingObjectData = parseCrossingObjectDataFromObjectDescription(objectDescription);
      const stationSection = getCrossingDisplayStationSection(crossingObjectData);

      if (stationSection) {
        return stationSection;
      }
    }
  }

  return formatDisplayValue(getObjectDescriptionField(application, "stationSection"));
};

export const getApplicationPaymentDisplay = (application: ApplicationsTableItem | undefined): string => {
  if (!application) {
    return "—";
  }

  const record = application as Record<string, unknown>;
  const displayPayment = record.displayPayment;

  if (typeof displayPayment === "string" && displayPayment.trim()) {
    return displayPayment;
  }

  const status = record.status as ApplicationStatus | undefined;

  if (status === "WAITING_PAYMENT") {
    const displayStatus = record.displayStatus;

    if (typeof displayStatus === "string" && displayStatus.trim()) {
      return displayStatus;
    }

    return APPLICATION_STATUS_FALLBACK_LABEL.WAITING_PAYMENT;
  }

  return "—";
};

export type ApplicationPaymentStatus = "NOT_REQUIRED" | "WAITING_PAYMENT" | "PAID";

export const APPLICATION_PAYMENT_STATUS_LABEL: Record<ApplicationPaymentStatus, string> = {
  NOT_REQUIRED: "Не требуется",
  WAITING_PAYMENT: "Ожидает оплаты",
  PAID: "Оплачено",
};

export const PAYMENT_FLAG_DISPLAY: Record<string, string> = {
  // «На рассмотрении» и прочерк — одно и то же (FRONT-1319): показываем просто прочерк.
  UNDER_REVIEW: "—",
  NO_PAYMENT: "Без оплаты",
  PAID: "Платная услуга",
};

export const getApplicationPaymentTermsDisplay = (application: ApplicationsTableItem | undefined): string => {
  if (!application) {
    return "—";
  }

  const record = application as Record<string, unknown>;
  // Внешний контур (GET /requests) отдаёт paymentCondition, внутренний (internal) — paymentFlag.
  const paymentFlag = record.paymentCondition ?? record.paymentFlag;

  if (typeof paymentFlag === "string" && paymentFlag.trim()) {
    return PAYMENT_FLAG_DISPLAY[paymentFlag] ?? paymentFlag;
  }

  const explicitTerms = record.paymentTerms ?? record.displayPaymentTerms;

  if (typeof explicitTerms === "string" && explicitTerms.trim()) {
    return explicitTerms;
  }

  if (record.status === "WAITING_PAYMENT") {
    return "По выставленному счету";
  }

  // TODO: удалить fallback, когда backend добавит условия оплаты в list API.
  return "—";
};

export const getApplicationPaymentStatus = (application: ApplicationsTableItem | undefined): ApplicationPaymentStatus | null => {
  if (!application) {
    return null;
  }

  const record = application as Record<string, unknown>;
  const explicitStatus = record.paymentStatus;

  if (explicitStatus === "PAID" || explicitStatus === "WAITING_PAYMENT" || explicitStatus === "NOT_REQUIRED") {
    return explicitStatus;
  }

  if (record.status === "WAITING_PAYMENT") {
    return "WAITING_PAYMENT";
  }

  // TODO: заменить на поле paymentStatus из backend list API.
  return null;
};

const formatLegacyApplicationType = (type: string): string => {
  if (type in APPLICATION_CATEGORY_LABEL) {
    return APPLICATION_CATEGORY_LABEL[type as keyof typeof APPLICATION_CATEGORY_LABEL];
  }

  if (type in APPLICATION_TYPE_FALLBACK_LABEL) {
    return APPLICATION_TYPE_FALLBACK_LABEL[type as CreateRequestBodyTypeEnumKey];
  }

  return type;
};

export const getApplicationTitleDisplay = (application: ApplicationsTableItem | undefined): string => {
  if (!application) {
    return "—";
  }

  const projectName = getObjectDescriptionField(application, "projectName");

  if (projectName) {
    return projectName;
  }

  const record = application as Record<string, unknown>;
  const title = typeof record.title === "string" ? record.title.trim() : "";

  if (!title) {
    return "—";
  }

  // Для незаполненного черновика бэкенд отдаёт сгенерированную заглушку «Новая заявка — <тип>».
  // По макету (BUG-3379) в этом случае показываем прочерк, а не заглушку.
  if (title === getGeneratedDraftTitle(application) || record.status === "DRAFT") {
    return "—";
  }

  return title;
};

export const getApplicationTypeDisplay = (application: ApplicationsTableItem | undefined): string => {
  if (!application) {
    return "—";
  }

  const record = application as Record<string, unknown>;
  const displayType = record.displayType;

  if (typeof displayType === "string" && displayType.trim()) {
    return displayType;
  }

  const objectDescription = record.objectDescription;

  if (isObjectRecord(objectDescription)) {
    const category = objectDescription.category;

    if (category === "adjacent" || category === "crossing") {
      return APPLICATION_CATEGORY_LABEL[category];
    }
  }

  const draftMeta = parseCreateApplicationAdditionalData(typeof record.additionalData === "string" ? record.additionalData : null);

  if (draftMeta?.categoryLabel?.trim()) {
    return draftMeta.categoryLabel;
  }

  if (draftMeta?.category) {
    return APPLICATION_CATEGORY_LABEL[draftMeta.category];
  }

  const type = record.type;

  if (typeof type === "string" && type.trim()) {
    return formatLegacyApplicationType(type);
  }

  return "—";
};

export const getApplicationApplicantDisplay = (application: ApplicationsTableItem | undefined): string => {
  if (!application) {
    return "—";
  }

  const record = application as Record<string, unknown>;
  const initiatorDisplay = getPersonDisplay(record.initiator);

  if (initiatorDisplay) {
    return initiatorDisplay;
  }

  const applicantName = record.applicantName;

  if (typeof applicantName === "string" && applicantName.trim()) {
    return applicantName;
  }

  if (isInternalApplication(application)) {
    return getInternalInitiatorDisplay(record);
  }

  if (isExternalApplication(application)) {
    return getExternalApplicantFio(application);
  }

  return "—";
};

export const getApplicationOperatorDisplay = (application: ApplicationsTableItem | undefined): string => {
  if (!application) {
    return "—";
  }

  const record = application as Record<string, unknown>;
  const operatorDisplay = getPersonDisplay(record.operator) ?? getPersonDisplay(record.operatorDisplay) ?? getPersonDisplay(record.operatorName);

  if (operatorDisplay) {
    return operatorDisplay;
  }

  return "—";
};

export const getApplicationUpdatedAtDisplay = (application: ApplicationsTableItem | undefined): string => {
  const record = (application ?? {}) as Record<string, unknown>;
  const updatedAt = record.updatedAt ?? record.resolvedAt ?? record.exportedAt ?? record.sentForReviewAt ?? record.createdAt ?? record.importedAt;

  return formatDateTime(updatedAt);
};

export const getApplicationReceivedAtDisplay = (application: ApplicationsTableItem | undefined): string => {
  const record = (application ?? {}) as Record<string, unknown>;

  return formatDateTime(record.receivedAt);
};

export const getApplicationProcessedAtDisplay = (application: ApplicationsTableItem | undefined): string => {
  const record = (application ?? {}) as Record<string, unknown>;
  const processedAt = record.processedAt ?? record.exportedAt ?? record.resolvedAt;

  return formatDateTime(processedAt);
};

export const getApplicationReviewDueAtDisplay = (application: ApplicationsTableItem | undefined): string => {
  const record = (application ?? {}) as Record<string, unknown>;
  const reviewDueDate = record.reviewDueDate;

  if (typeof reviewDueDate === "string" && reviewDueDate) {
    return formatDate(reviewDueDate);
  }

  const reviewDueAt = record.reviewDueAt ?? record.reviewByAt ?? record.considerByAt ?? record.displayReviewDueAt;

  return formatDateTime(reviewDueAt);
};

export const getApplicationResponsibleDisplay = (application: ApplicationsTableItem | undefined): string => {
  if (!application) {
    return "—";
  }

  const record = application as Record<string, unknown>;
  const responsibleDisplay =
    getPersonDisplay(record.currentAssignee) ??
    getPersonDisplay(record.assignee) ??
    getPersonDisplay(record.responsibleDisplay) ??
    getPersonDisplay(record.responsiblePerson) ??
    getPersonDisplay(record.responsibleName) ??
    getPersonDisplay(record.responsible);

  if (responsibleDisplay) {
    return responsibleDisplay;
  }

  return "—";
};

export const getApplicationApplicantOrganizationDisplay = (application: ApplicationsTableItem | undefined): string => {
  if (!application) {
    return "—";
  }

  const record = application as Record<string, unknown>;
  const organizationDisplay = record.applicantOrganization ?? record.organizationDisplay ?? record.applicantName;

  if (typeof organizationDisplay === "string" && organizationDisplay.trim()) {
    return organizationDisplay;
  }

  if (isInternalApplication(application)) {
    return getInternalInitiatorDisplay(record);
  }

  // В колонке «Заявитель» показываем только то, что указано в самой заявке.
  // Подставлять профиль пользователя (ФИО из Keycloak) нельзя — по макету (BUG-3379) выводим прочерк.
  return "—";
};

export const getApplicationInitiatorPersonDisplay = (application: ApplicationsTableItem | undefined): string => {
  if (!application) {
    return "—";
  }

  const record = application as Record<string, unknown>;
  const initiatorPerson =
    getPersonDisplay(record.initiator) ??
    getPersonDisplay(record.initiatorFio) ??
    getPersonDisplay(record.initiatorName) ??
    getPersonDisplay(record.initiatorPersonDisplay);

  if (initiatorPerson) {
    return initiatorPerson;
  }

  if (isInternalApplication(application)) {
    return getInternalInitiatorDisplay(record);
  }

  if (isExternalApplication(application)) {
    return getExternalApplicantFio(application);
  }

  return "—";
};

export type ApplicationDeadlineControlState = InternalRequestDetailResponseDeadlineControlEnumKey;

const formatDeadlineControlDaysLabel = (remainingDays: number): string =>
  remainingDays < 0 ? `Просрочено на ${Math.abs(remainingDays)} дн.` : `Осталось ${remainingDays} дн.`;

export const getApplicationDeadlineControlDisplay = (
  application: ApplicationsTableItem | undefined,
): { label: string; state: ApplicationDeadlineControlState } | null => {
  if (!application) {
    return null;
  }

  const record = application as Record<string, unknown>;
  const deadlineControl = record.deadlineControl as ApplicationDeadlineControlState | undefined;

  if (!deadlineControl) {
    return null;
  }

  if (deadlineControl === "REVIEWED") {
    return { label: "Рассмотрена", state: "REVIEWED" };
  }

  const remainingDays = record.remainingDays;

  if (typeof remainingDays !== "number") {
    return null;
  }

  return { label: formatDeadlineControlDaysLabel(remainingDays), state: deadlineControl };
};

export type ApplicationProcessingState = "REQUIRES_EXPORT" | "EXPORTED" | "IMPORTED";

export const APPLICATION_PROCESSING_STATE_LABEL: Record<ApplicationProcessingState, string> = {
  REQUIRES_EXPORT: "Требуется выгрузка",
  EXPORTED: "Выгружено",
  IMPORTED: "Загружено",
};

const normalizeProcessingState = (value: unknown): ApplicationProcessingState | null => {
  if (value === "REQUIRES_EXPORT" || value === "NEEDS_EXPORT") {
    return "REQUIRES_EXPORT";
  }

  if (value === "EXPORTED") {
    return "EXPORTED";
  }

  if (value === "IMPORTED" || value === "LOADED") {
    return "IMPORTED";
  }

  return null;
};

export const COLUMN_SORT_FIELD_MAP: Record<string, GetRequestsSortByKey> = {
  number: "NUMBER",
  title: "TITLE",
  type: "TYPE",
  status: "STATUS",
  railway: "RAILWAY",
  section: "SECTION",
  importedAt: "IMPORTED_AT",
  createdAt: "CREATED_AT",
  updatedAt: "UPDATED_AT",
  applicantName: "APPLICANT_NAME",
  initiator: "INITIATOR",
  processingFlag: "PROCESSING_FLAG",
  processedAt: "PROCESSED_AT",
  operator: "OPERATOR",
  reviewDueDate: "REVIEW_DUE_DATE",
  responsible: "ASSIGNEE",
  deadlineControl: "DEADLINE_CONTROL",
  payment: "PAYMENT_FLAG",
};

export const EXTERNAL_APPLICANT_SORT_FIELD_MAP: Record<string, ExternalGetRequestsSortByKey> = {
  number: "NUMBER",
  title: "TITLE",
  type: "REQUEST_SUBJECT",
  status: "STATUS",
  railway: "RAILWAY",
  createdAt: "CREATED_AT",
  updatedAt: "UPDATED_AT",
  payment: "PAYMENT_CONDITION",
  applicantName: "APPLICANT_NAME",
};

export const EXTERNAL_OPERATOR_SORT_FIELD_MAP: Record<string, GetOperatorRequestsSortByKey> = {
  number: "NUMBER",
  title: "TITLE",
  importedAt: "RECEIVED_AT",
  processingFlag: "PROCESSING_FLAG",
  processedAt: "PROCESSED_AT",
  railway: "RAILWAY",
  status: "STATUS",
  operator: "OPERATOR",
  initiator: "INITIATOR",
};

export const getApplicationProcessingState = (application: ApplicationsTableItem | undefined): ApplicationProcessingState | null => {
  if (!application) {
    return null;
  }

  const record = application as Record<string, unknown>;
  const explicitState = normalizeProcessingState(record.processingFlag) ?? normalizeProcessingState(record.operatorState);

  if (explicitState) {
    return explicitState;
  }

  if (isInternalApplication(application)) {
    const status = record.status as ApplicationStatus | undefined;

    if (status !== undefined && (INTERNAL_OPERATOR_FINAL_STATUSES as readonly ApplicationStatus[]).includes(status)) {
      return "EXPORTED";
    }

    return "IMPORTED";
  }

  return null;
};

export const getApplicationDetailProcessingState = (application: { processingFlag?: string | null } | undefined): ApplicationProcessingState | null =>
  normalizeProcessingState(application?.processingFlag);

const APPLICANT_TABLE_COLUMNS: ColDef<ApplicationsTableItem>[] = [
  {
    headerName: "№",
    colId: "number",
    field: "number",
    valueFormatter: ({ value }) => formatApplicationNumber(value),
    width: 107,
    minWidth: 107,
    lockVisible: true,
  },
  {
    headerName: "Название заявки",
    colId: "title",
    field: "title",
    valueGetter: ({ data }) => getApplicationTitleDisplay(data),
    minWidth: 320,
    flex: 2,
    lockVisible: true,
  },
  {
    headerName: "Тип заявки",
    colId: "type",
    field: "type",
    valueGetter: ({ data }) => getApplicationTypeDisplay(data),
    valueFormatter: ({ value }) => value || "—",
    minWidth: 137,
  },
  {
    headerName: "Статус заявки",
    colId: "status",
    field: "status",
    cellRenderer: ApplicationStatusCell,
    minWidth: 220,
  },
  {
    field: "railway",
    colId: "railway",
    headerName: "Железная дорога",
    cellRenderer: ApplicationRailwayCell,
    minWidth: 160,
  },
  {
    headerName: "Условия оплаты",
    colId: "payment",
    field: "paymentCondition",
    valueGetter: ({ data }) => getApplicationPaymentTermsDisplay(data),
    valueFormatter: ({ value }) => value || "—",
    minWidth: 200,
    sortable: false,
  },
  {
    headerName: "Заявитель",
    colId: "applicantName",
    valueGetter: ({ data }) => getApplicationApplicantOrganizationDisplay(data),
    valueFormatter: ({ value }) => value || "—",
    minWidth: 200,
  },
  {
    headerName: "Дата создания",
    colId: "createdAt",
    valueGetter: ({ data }) => getCreatedOrImportedAt(data),
    valueFormatter: ({ value }) => formatDateTime(value),
    minWidth: 160,
  },
  {
    headerName: "Дата изменения",
    colId: "updatedAt",
    valueGetter: ({ data }) => getApplicationUpdatedAtDisplay(data),
    valueFormatter: ({ value }) => value || "—",
    minWidth: 160,
  },
];

const OPERATOR_TABLE_COLUMNS: ColDef<ApplicationsTableItem>[] = [
  {
    headerName: "№",
    colId: "number",
    field: "number",
    valueFormatter: ({ value }) => formatApplicationNumber(value),
    width: 107,
    minWidth: 107,
    lockVisible: true,
  },
  {
    headerName: "Название заявки",
    colId: "title",
    field: "title",
    valueGetter: ({ data }) => getApplicationTitleDisplay(data),
    minWidth: 320,
    flex: 2,
    lockVisible: true,
  },
  {
    headerName: "Дата поступления",
    colId: "importedAt",
    field: "importedAt",
    valueGetter: ({ data }) => getApplicationReceivedAtDisplay(data),
    valueFormatter: ({ value }) => value || "—",
    minWidth: 220,
  },
  {
    headerName: "Признак обработки",
    colId: "processingFlag",
    field: "processingFlag",
    cellRenderer: ApplicationProcessingStatusCell,
    minWidth: 210,
    sortable: false,
  },
  {
    headerName: "Статус заявки",
    colId: "status",
    field: "status",
    cellRenderer: ApplicationStatusCell,
    minWidth: 220,
  },
  {
    headerName: "Дата обработки",
    colId: "processedAt",
    field: "processedAt",
    valueGetter: ({ data }) => getApplicationProcessedAtDisplay(data),
    valueFormatter: ({ value }) => value || "—",
    minWidth: 180,
    sortable: false,
  },
  {
    headerName: "Оператор",
    colId: "operator",
    valueGetter: ({ data }) => getApplicationOperatorDisplay(data),
    valueFormatter: ({ value }) => value || "—",
    minWidth: 250,
    sortable: false,
  },
  {
    colId: "railway",
    field: "railway",
    headerName: "Железная дорога",
    cellRenderer: ApplicationRailwayCell,
    minWidth: 210,
  },
  {
    headerName: "Инициатор",
    colId: "initiator",
    field: "initiator",
    valueGetter: ({ data }) => getApplicationApplicantDisplay(data),
    valueFormatter: ({ value }) => value || "—",
    minWidth: 200,
  },
];

const INTERNAL_NG_SECRETARY_TABLE_COLUMNS: ColDef<ApplicationsTableItem>[] = [
  {
    headerName: "№",
    colId: "number",
    field: "number",
    valueFormatter: ({ value }) => formatApplicationNumber(value),
    width: 107,
    minWidth: 107,
    lockVisible: true,
  },
  {
    headerName: "Название заявки",
    colId: "title",
    field: "title",
    valueGetter: ({ data }) => getApplicationTitleDisplay(data),
    minWidth: 320,
    flex: 2,
    lockVisible: true,
  },
  {
    headerName: "Тип заявки",
    colId: "type",
    field: "type",
    valueGetter: ({ data }) => getApplicationTypeDisplay(data),
    valueFormatter: ({ value }) => value || "—",
    minWidth: 137,
  },
  {
    headerName: "Дата поступления",
    colId: "importedAt",
    field: "importedAt",
    valueGetter: ({ data }) => getApplicationReceivedAtDisplay(data),
    valueFormatter: ({ value }) => value || "—",
    minWidth: 215,
  },
  {
    headerName: "Рассмотреть до",
    colId: "reviewDueDate",
    field: "reviewDueDate",
    valueGetter: ({ data }) => getApplicationReviewDueAtDisplay(data),
    valueFormatter: ({ value }) => value || "—",
    minWidth: 215,
    sortable: false,
  },
  {
    headerName: "Статус рассмотрения",
    colId: "status",
    field: "status",
    cellRenderer: ApplicationStatusCell,
    minWidth: 309,
  },
  {
    headerName: "Ответственный",
    colId: "responsible",
    field: "assignee",
    valueGetter: ({ data }) => getApplicationResponsibleDisplay(data),
    valueFormatter: ({ value }) => value || "—",
    minWidth: 174,
    sortable: false,
  },
  {
    headerName: "Контроль срока",
    colId: "deadlineControl",
    cellRenderer: ApplicationDeadlineControlCell,
    minWidth: 192,
    sortable: false,
  },
  {
    colId: "railway",
    field: "railway",
    headerName: "Железная дорога",
    cellRenderer: ApplicationRailwayCell,
    minWidth: 130,
  },
  {
    headerName: "Станция / перегон",
    colId: "section",
    field: "section",
    valueGetter: ({ data }) => getApplicationStationSectionDisplay(data),
    valueFormatter: ({ value }) => value || "—",
    minWidth: 200,
  },
  {
    headerName: "Условия оплаты",
    colId: "payment",
    field: "paymentFlag",
    valueGetter: ({ data }) => getApplicationPaymentTermsDisplay(data),
    valueFormatter: ({ value }) => value || "—",
    minWidth: 200,
    sortable: false,
  },
  {
    headerName: "Заявитель",
    colId: "applicantName",
    field: "applicantName",
    valueGetter: ({ data }) => getApplicationApplicantOrganizationDisplay(data),
    valueFormatter: ({ value }) => value || "—",
    minWidth: 200,
  },
  {
    headerName: "Инициатор",
    colId: "initiator",
    field: "initiator",
    valueGetter: ({ data }) => getApplicationInitiatorPersonDisplay(data),
    valueFormatter: ({ value }) => value || "—",
    minWidth: 170,
  },
  {
    headerName: "Дата создания",
    colId: "createdAt",
    field: "createdAt",
    valueGetter: ({ data }) => getCreatedOrImportedAt(data),
    valueFormatter: ({ value }) => formatDateTime(value),
    minWidth: 170,
  },
  {
    headerName: "Дата изменения",
    colId: "updatedAt",
    field: "updatedAt",
    valueGetter: ({ data }) => getApplicationUpdatedAtDisplay(data),
    valueFormatter: ({ value }) => value || "—",
    minWidth: 170,
  },
];

export type ApplicationsTableVariant = "applicant" | "operator" | "ng-secretary";

export const getApplicationsTableColumns = (variant: ApplicationsTableVariant): ColDef<ApplicationsTableItem>[] => {
  switch (variant) {
    case "operator":
      return OPERATOR_TABLE_COLUMNS;
    case "ng-secretary":
      return INTERNAL_NG_SECRETARY_TABLE_COLUMNS;
    default:
      return APPLICANT_TABLE_COLUMNS;
  }
};

export const TABLE_ROWS_PER_PAGE = 30;

export const DEFAULT_COL_DEF: ColDef<ApplicationsTableItem> = {
  flex: 1,
  cellRenderer: CellWithTooltip,
  cellClass: "flex items-center",
  sortable: true,
};

export const REQUEST_SUBJECT_FILTER_OPTIONS: TableListColumnFilterItem[] = [
  { id: "ADJOINING", label: "Примыкание" },
  { id: "CROSSING", label: "Пересечение" },
];

export const INTERNAL_STATUS_FILTER_OPTIONS: TableListColumnFilterItem[] = Object.entries(INTERNAL_APPLICATION_STATUS_FALLBACK_LABEL).map(
  ([id, label]) => ({ id, label }),
);

export const EXTERNAL_STATUS_FILTER_OPTIONS: TableListColumnFilterItem[] = Object.entries(EXTERNAL_APPLICATION_STATUS_FALLBACK_LABEL).map(
  ([id, label]) => ({ id, label }),
);

export const PAYMENT_FLAG_FILTER_OPTIONS: TableListColumnFilterItem[] = Object.entries(PAYMENT_FLAG_DISPLAY).map(([id, label]) => ({
  id,
  label,
}));

export const mapPaymentFlagFilterIdsToQuery = (ids: string[]): string[] | undefined => {
  if (!ids.length) {
    return undefined;
  }

  return ids.map((id) => (id === EMPTY_VALUE_KEY ? "" : id));
};

/** Опция «Не назначен» в фильтре «Ответственный» (в ячейке — прочерк; filter-values её не отдаёт). */
export const ASSIGNEE_UNASSIGNED_FILTER_ITEM: TableListColumnFilterItem = { id: EMPTY_VALUE_KEY, label: "—" };

/**
 * Маппинг фильтра «Ответственный» → query GET /requests:
 * прочерк → assigneeUnassigned=true; остальное → assigneeIds (ИЛИ с unassigned; пустой массив не шлём).
 */
export const mapAssigneeFilterIdsToQuery = (ids: string[]): { assigneeIds?: string[]; assigneeUnassigned?: boolean } => {
  const assigneeUnassigned = ids.includes(EMPTY_VALUE_KEY);
  const assigneeIds = ids.filter((id) => id !== EMPTY_VALUE_KEY);

  return {
    assigneeIds: assigneeIds.length > 0 ? assigneeIds : undefined,
    assigneeUnassigned: assigneeUnassigned || undefined,
  };
};

export const PROCESSING_FLAG_FILTER_OPTIONS: TableListColumnFilterItem[] = [
  { id: "NEEDS_EXPORT", label: "Требуется выгрузка" },
  { id: "EXPORTED", label: "Выгружено" },
  { id: "LOADED", label: "Загружено" },
];

export const DEADLINE_CONTROL_FILTER_OPTIONS: TableListColumnFilterItem[] = [
  { id: "PLANNED", label: "Плановый срок" },
  { id: "DEADLINE_APPROACHING", label: "Приближается срок" },
  { id: "RISK_OF_VIOLATION", label: "Риск нарушения" },
  { id: "OVERDUE", label: "Просрочено" },
  { id: "REVIEWED", label: "Рассмотрена" },
];

// ---------------------------------- STORE ----------------------------------

class PageStore {
  public popupAction: PopupAction | null = null;
  public tableApi: GridApi<ApplicationsTableItem> | null = null;
  public totalItemsCount = 0;
  public selectedApplications: ApplicationsTableItem[] = [];
  public statCardFilterKey = "TOTAL";
  public tableFilter: StatCardTableFilter = { type: "none" };
  public refreshVersion = 0;

  constructor() {
    makeAutoObservable(this);
  }

  public get selectedApplicationIds(): string[] {
    return this.selectedApplications.map((application) => application.id).filter((id): id is string => Boolean(id));
  }

  public setPopupAction(action: PopupAction | null) {
    this.popupAction = action;
  }

  public setTableApi(api: GridApi<ApplicationsTableItem> | null) {
    this.tableApi = api;
  }

  public setTotalItemsCount(count: number) {
    this.totalItemsCount = count;
  }

  public setSelectedApplications(applications: ApplicationsTableItem[]) {
    this.selectedApplications = applications;
  }

  public setStatCardFilter(filterKey: string, tableFilter: StatCardTableFilter) {
    this.statCardFilterKey = filterKey;
    this.tableFilter = tableFilter;
  }

  public clearSelectedApplications() {
    this.selectedApplications = [];
  }

  public refreshApplications() {
    this.refreshVersion += 1;
  }
}

const { StoreProvider: PageStoreProvider, useStore: usePageStore } = makeStoreProvider(PageStore);

export { PageStoreProvider, usePageStore };
